import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';
import {
  Brain, Loader2, Zap, Target, TrendingUp, RefreshCw,
  Award, ChevronDown, BarChart3,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ValidationStatsPanel } from '../../components/admin/ValidationStatsPanel';
import { PredictionHistory } from '../../components/admin/PredictionHistory';

const AdminSuperIAMaster = () => {
  const { token } = useAuth();
  const [lotteries, setLotteries] = useState([]);
  const [selectedLottery, setSelectedLottery] = useState('');
  const [generating, setGenerating] = useState(false);
  const [prediction, setPrediction] = useState(null);
  const [history, setHistory] = useState([]);
  const [validationStats, setValidationStats] = useState(null);

  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchLotteries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/lotteries`, { headers: getHeaders() });
      const list = res.data.lotteries || res.data || [];
      setLotteries(list);
      if (list.length > 0) setSelectedLottery(list[0].id || list[0].lottery_id);
    } catch (e) { toast.error('Error al cargar loterias'); }
  }, [getHeaders]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/super-ia-master/history`, { headers: getHeaders() });
      setHistory(res.data.predictions || res.data || []);
    } catch (e) { console.error('Error fetching history:', e); }
  }, [getHeaders]);

  const fetchValidationStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/super-ia-master/validate`, { headers: getHeaders() });
      setValidationStats(res.data);
    } catch (e) { console.error('Error fetching validation stats:', e); }
  }, [getHeaders]);

  useEffect(() => {
    fetchLotteries();
    fetchHistory();
    fetchValidationStats();
  }, [fetchLotteries, fetchHistory, fetchValidationStats]);

  const generatePrediction = async () => {
    if (!selectedLottery) { toast.error('Selecciona una loteria'); return; }
    setGenerating(true);
    setPrediction(null);
    try {
      const res = await axios.post(
        `${API}/admin/super-ia-master/predict`,
        { lottery_id: selectedLottery },
        { headers: getHeaders() }
      );
      setPrediction(res.data);
      toast.success('Prediccion Master generada!');
      fetchHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al generar prediccion');
    } finally { setGenerating(false); }
  };

  const selectedLotteryName = lotteries.find(l => (l.id || l.lottery_id) === selectedLottery)?.name || '';

  return (
    <AdminLayout title="Sócrates Master" subtitle="Motor de prediccion avanzado V6">
      <div className="space-y-8">
        {/* Generation Card */}
        <Card className="bg-gradient-to-r from-purple-900/30 to-cyan-900/30 border-purple-500/20" data-testid="generate-card">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center gap-4">
              <Brain size={40} className="text-purple-400" />
              <div className="flex-1">
                <h2 className="text-xl font-bold text-white">Generar Prediccion Master</h2>
                <p className="text-gray-400 text-sm">Motor V6: Terminales (65%) + Vecinos (27%) + Posicional (23%) + Sócrates</p>
              </div>
              <Select value={selectedLottery} onValueChange={setSelectedLottery}>
                <SelectTrigger className="w-[220px] bg-gray-900 border-gray-700 text-white" data-testid="lottery-select">
                  <SelectValue placeholder="Seleccionar loteria" />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-700">
                  {lotteries.map((l) => (
                    <SelectItem key={l.id || l.lottery_id} value={l.id || l.lottery_id} className="text-white hover:bg-gray-800">
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={generatePrediction} disabled={generating || !selectedLottery}
                className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-500 hover:to-cyan-500 text-white px-8"
                data-testid="generate-btn">
                {generating ? <Loader2 size={18} className="animate-spin mr-2" /> : <Zap size={18} className="mr-2" />}
                Generar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Prediction Result */}
        {prediction && (
          <Card className="bg-gray-900 border-gray-800 overflow-hidden" data-testid="prediction-result">
            <div className="h-1 bg-gradient-to-r from-purple-500 via-cyan-500 to-green-500" />
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold text-white">{selectedLotteryName}</h3>
                  <p className="text-gray-500 text-sm">{prediction.date || new Date().toLocaleDateString('es-DO')}</p>
                </div>
                <Badge className={`text-sm ${
                  (prediction.confidence || 0) >= 80 ? 'bg-green-500/20 text-green-400'
                  : (prediction.confidence || 0) >= 50 ? 'bg-amber-500/20 text-amber-400'
                  : 'bg-red-500/20 text-red-400'
                }`}>
                  Confianza: {prediction.confidence || 0}%
                </Badge>
              </div>
              <div className="flex justify-center gap-4 mb-6">
                {(prediction.predicted_numbers || prediction.numbers)?.map((num, idx) => (
                  <div key={`pred-${num}-${idx}`} className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500/30 to-cyan-600/30 border border-purple-500/40 flex items-center justify-center shadow-lg">
                    <span className="text-2xl font-black text-white">{String(num).padStart(2, '0')}</span>
                  </div>
                ))}
              </div>
              {(prediction.explanation || prediction.analysis?.primary_reason) && (
                <div className="p-4 rounded-xl bg-gray-800/50 border border-gray-700/30">
                  <p className="text-gray-300 text-sm">{prediction.explanation || prediction.analysis?.primary_reason}</p>
                </div>
              )}
              {prediction.analysis && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                  {Object.entries(prediction.analysis).slice(0, 8).map(([key, val]) => (
                    <div key={key} className="p-3 rounded-lg bg-gray-800/30 text-center">
                      <p className="text-gray-500 text-xs capitalize">{key.replace(/_/g, ' ')}</p>
                      <p className="text-white font-bold text-sm">{typeof val === 'number' ? val.toFixed?.(2) || val : String(val).slice(0, 20)}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs defaultValue="stats" className="space-y-6">
          <TabsList className="bg-gray-900 border border-gray-800 p-1">
            <TabsTrigger value="stats" className="data-[state=active]:bg-green-500/20 data-[state=active]:text-green-400" data-testid="tab-stats">
              <BarChart3 size={16} className="mr-2" /> Validaciones
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400" data-testid="tab-history">
              <Award size={16} className="mr-2" /> Historial
            </TabsTrigger>
          </TabsList>
          <TabsContent value="stats">
            <ValidationStatsPanel validationStats={validationStats} />
          </TabsContent>
          <TabsContent value="history">
            <PredictionHistory history={history} />
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminSuperIAMaster;
